/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant } from 'js-joda';
import _ from 'lodash';

import { StoreType } from '@tock/shared/utils/connect';
import { calcNumPendingMessagesFromCommunicator } from 'selectors/service/numPendingMessagesSelector';
import { constructTopic } from 'utils/service/topicUtils';
import { getRandomString } from 'utils/service/random';
import { logger } from '@tock/logging';

declare let localStorage: any;

/**
 * KEEP
 * Defines, for the websocket, a unique identifier for this device.
 */
const CLIENT_ID_KEY = 'clientId';

/**
 * Saves the Communicator state (Communicator.elm) so if we refresh
 * we get a faster boot up. In particular, saves the number of pending
 * messages.
 * Kept around for migration from legacy reasons.
 * Can be removed in August 2018
 * TODO (YK 2018/06/15)
 */
const COMMUNICATOR_KEY = 'communicatorState';

/**
 * KEEP
 * Stores the same communicator state as COMMUNICATOR_KEY, but keyed by
 * topic so that multiple can be stored.
 */
const TOPIC_KEYED_COMMUNICATORS_KEY = 'communicatorsByTopic';

/**
 * KEEP
 * Stores the results of a fetch from the service config table.
 * Allows for faster appearance of configured icons and business defined states,
 * among other things.
 */
const CONFIG_KEY = 'serviceConfig';

/**
 * Tracks the currently selected topic in the elm model. We already track
 * the selected business in the active business cookie. The selected date
 * we could infer from the URL.
 * In any case, the goal is to allow multiple of these.
 */
const CURRENT_TOPIC_KEY = 'currentTopic';

/**
 * KEEP
 * Used to keep the user in "Observation mode" across refreshes.
 */
const IS_EMPLOYEE_KEY = 'isReadOnly';

/**
 * KEEP (it's already keyed by topic)
 * Last server id seen is used to know where to catch up from if
 * we briefly go offline, as well as to check for "collisions".
 * Storing it in local storage allows us to pick up where we left off.
 * This is made significantly less important by the BaseState in the database
 */
const LAST_SERVER_ID_SEEN_KEY = 'lastServerIdSeen';

/**
 * KEEP
 * Used to paper over an issue where the user was being asked to
 * refresh w/ a new version available over and over. We track when
 * they last saw it so we can suppress the message if it was too recent.
 */
const LAST_UPDATE_REQUEST_TIME_SECS_KEY = 'lastUpdateRequestTimeSecs';

/**
 * KEEP
 * This stores information about the current UI state of service so
 * that it can be sticky across refreshes and tab changes. For example,
 * which room is selected, which view is selected, etc.
 * The SaveableUIState type in state/service/ui.ts has the definitive list
 */
const UI_STATE_KEY = 'savedUIState';

const GUID_LENGTH = 32;

/**
 * Stores a boolean indicating whether notification sounds for To Go orders should
 * be enabled on this device.
 */
const NOTIFICATION_SOUNDS_ENABLED_KEY = 'notificationSoundsEnabled';

export function clearEventStreamState() {
  removeItemFromLocalStorage(CLIENT_ID_KEY);
  removeItemFromLocalStorage(COMMUNICATOR_KEY);
  removeItemFromLocalStorage(TOPIC_KEYED_COMMUNICATORS_KEY);
  removeItemFromLocalStorage(LAST_SERVER_ID_SEEN_KEY);
  removeItemFromLocalStorage(CONFIG_KEY);
  markCurrentTopicAsNeverConnected();
}

export function clearLastAcknowledgedServerEventId() {
  removeItemFromLocalStorage(LAST_SERVER_ID_SEEN_KEY);
}

function markCurrentTopicAsNeverConnected() {
  const currentTopic = getStoreValue(CURRENT_TOPIC_KEY, 'currentTopic');
  const updatedCurrentTopic = {
    ...currentTopic,
    everConnected: false,
  };
  setItemInLocalStorage(CURRENT_TOPIC_KEY, JSON.stringify(updatedCurrentTopic));
}

export function getClientId() {
  let clientId = localStorage.getItem(CLIENT_ID_KEY);
  if (!clientId) {
    clientId = getRandomString(GUID_LENGTH);
    setItemInLocalStorage(CLIENT_ID_KEY, clientId);
  }
  return clientId;
}

export function getConfig() {
  return getStoreValue(CONFIG_KEY, 'config');
}

export function getLastAcknowledgedServerEventId(topic: any) {
  const storedVal: Dictionary<number> =
    getStoreValue(LAST_SERVER_ID_SEEN_KEY, 'lastServerIdSeen') || {};
  return storedVal[topic] || 0;
}

export function getLastUpdateRequestTime(): Instant | undefined {
  const storedVal = getStoreValue(LAST_UPDATE_REQUEST_TIME_SECS_KEY, 'lastUpdateRequestTimeSecs');
  if (!storedVal) {
    return undefined;
  }
  try {
    return Instant.ofEpochSecond(storedVal);
  } catch (e) {
    // In case storage is corrupted somehow, don't explode
    removeItemFromLocalStorage(LAST_UPDATE_REQUEST_TIME_SECS_KEY);
    logger.error('Error reading last update request time', storedVal, e);
    return undefined;
  }
}

export function getNotificationSoundsEnabled(): boolean {
  return localStorage.getItem(NOTIFICATION_SOUNDS_ENABLED_KEY) === 'true';
}

export function setLastUpdateRequestTime(time: Instant) {
  setItemInLocalStorage(LAST_UPDATE_REQUEST_TIME_SECS_KEY, time.epochSecond());
}

export function setNotificationSoundsEnabled(enabled: boolean): void {
  setItemInLocalStorage(NOTIFICATION_SOUNDS_ENABLED_KEY, enabled);
}

export function getCommunicatorsByTopic(): any | undefined {
  return getStoreValue(TOPIC_KEYED_COMMUNICATORS_KEY, 'communicatorsByTopic');
}

export function getNonAppState() {
  const legacyCommunicator = getStoreValue(COMMUNICATOR_KEY, 'communicator');
  const communicators = getStoreValue(TOPIC_KEYED_COMMUNICATORS_KEY, 'communicatorsByTopic');
  const currentTopic = getStoreValue(CURRENT_TOPIC_KEY, 'currentTopic');
  const isReadOnly = getStoreValue(IS_EMPLOYEE_KEY, 'isReadOnly');
  return {
    legacyCommunicator,
    communicators,
    currentTopic,
    isReadOnly,
  };
}

export function getUIState() {
  return getStoreValue(UI_STATE_KEY, 'savedUIState');
}

export function saveConfig(config: any) {
  if (!config) {
    return;
  }
  setItemInLocalStorage(CONFIG_KEY, JSON.stringify(config));
}

export function saveLastAcknowledgedServerEventId(topic: any, id: number) {
  if (!topic || !id) {
    return;
  }
  setItemInLocalStorage(LAST_SERVER_ID_SEEN_KEY, JSON.stringify({ [topic]: id }));
}

export function saveNonAppState(state: StoreType['service']['app']) {
  if (!state || !state.currentTopic) {
    return;
  }
  const topicKey = constructTopic(state.currentTopic);
  const existingCommunicators = getStoreValue(TOPIC_KEYED_COMMUNICATORS_KEY, 'communicators');
  const existingCommunicatorsWithPending = _.pickBy(existingCommunicators, (communicator) => {
    return calcNumPendingMessagesFromCommunicator(communicator) > 0;
  });
  const updatedCommunicatorWithPending = {
    ...existingCommunicatorsWithPending,
    [topicKey]: state.communicator,
  };
  setItemInLocalStorage(
    TOPIC_KEYED_COMMUNICATORS_KEY,
    JSON.stringify(updatedCommunicatorWithPending)
  );
  setItemInLocalStorage(CURRENT_TOPIC_KEY, JSON.stringify(state.currentTopic));
  setItemInLocalStorage(IS_EMPLOYEE_KEY, JSON.stringify(state.isReadOnly));
}

export function saveUIState(uiState: any) {
  if (!uiState) {
    return;
  }
  setItemInLocalStorage(UI_STATE_KEY, JSON.stringify(uiState));
}

function removeItemFromLocalStorage(key: string) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    logger.warn(`Unable to remove key ${key} from localStorage`, e);
  }
  return false;
}

function setItemInLocalStorage(key: string, value: any) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    logger.warn(`Unable to set key ${key} in localStorage`, e);
  }
  return false;
}

// Helper functions below
function getStoreValue(key: string, name: any) {
  try {
    if (!_.isNil(localStorage.getItem(key))) {
      return JSON.parse(localStorage.getItem(key));
    }
    return undefined;
  } catch (e) {
    logger.error(`Error loading ${name} state`, e);
    removeItemFromLocalStorage(key);
    return undefined;
  }
}
